import { COUNTRIES } from "./countries";

// País para Viva Cine deducido 100% en el dispositivo — sin enviar la IP ni
// ningún otro dato a un tercero (antes se consultaba ip-api.com desde el
// backend; ver Ley 21.719). Orden de señales:
//   1. Zona horaria del dispositivo (la más fiel a "dónde estoy": un
//      teléfono en Chile tiene America/Santiago aunque el idioma sea en-US).
//   2. Región del idioma preferido ("es-CL" → CL).
//   3. Chile por defecto.
// El usuario siempre puede cambiar el país a mano en el selector.
// Mantener idéntico en ciudad-viva y ciudad-viva-mobile.

const DEFAULT_REGION = "CL";

const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  "America/Santiago": "CL", "America/Punta_Arenas": "CL", "Pacific/Easter": "CL",
  "America/La_Paz": "BO",
  "America/Sao_Paulo": "BR", "America/Noronha": "BR", "America/Belem": "BR",
  "America/Fortaleza": "BR", "America/Recife": "BR", "America/Araguaina": "BR",
  "America/Maceio": "BR", "America/Bahia": "BR", "America/Campo_Grande": "BR",
  "America/Cuiaba": "BR", "America/Santarem": "BR", "America/Porto_Velho": "BR",
  "America/Boa_Vista": "BR", "America/Manaus": "BR", "America/Eirunepe": "BR",
  "America/Rio_Branco": "BR",
  "America/Toronto": "CA", "America/Vancouver": "CA", "America/Edmonton": "CA",
  "America/Winnipeg": "CA", "America/Halifax": "CA", "America/St_Johns": "CA",
  "America/Regina": "CA", "America/Moncton": "CA", "America/Glace_Bay": "CA",
  "America/Goose_Bay": "CA", "America/Whitehorse": "CA", "America/Dawson": "CA",
  "America/Dawson_Creek": "CA", "America/Fort_Nelson": "CA", "America/Iqaluit": "CA",
  "America/Rankin_Inlet": "CA", "America/Resolute": "CA", "America/Cambridge_Bay": "CA",
  "America/Inuvik": "CA", "America/Swift_Current": "CA", "America/Atikokan": "CA",
  "America/Creston": "CA", "America/Blanc-Sablon": "CA",
  "America/Bogota": "CO",
  "America/Costa_Rica": "CR",
  "America/Havana": "CU",
  "Europe/Berlin": "DE", "Europe/Busingen": "DE",
  "America/Santo_Domingo": "DO",
  "America/Guayaquil": "EC", "Pacific/Galapagos": "EC",
  "Europe/Madrid": "ES", "Africa/Ceuta": "ES", "Atlantic/Canary": "ES",
  "Europe/Paris": "FR",
  "Europe/London": "GB",
  "America/Guatemala": "GT",
  "America/Tegucigalpa": "HN",
  "Europe/Rome": "IT",
  "America/Mexico_City": "MX", "America/Cancun": "MX", "America/Merida": "MX",
  "America/Monterrey": "MX", "America/Matamoros": "MX", "America/Chihuahua": "MX",
  "America/Ciudad_Juarez": "MX", "America/Ojinaga": "MX", "America/Mazatlan": "MX",
  "America/Bahia_Banderas": "MX", "America/Hermosillo": "MX", "America/Tijuana": "MX",
  "America/Managua": "NI",
  "America/Panama": "PA",
  "America/Lima": "PE",
  "America/Puerto_Rico": "PR",
  "Europe/Lisbon": "PT", "Atlantic/Madeira": "PT", "Atlantic/Azores": "PT",
  "America/Asuncion": "PY",
  "America/El_Salvador": "SV",
  "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US",
  "America/Los_Angeles": "US", "America/Phoenix": "US", "America/Anchorage": "US",
  "America/Detroit": "US", "America/Boise": "US", "America/Juneau": "US",
  "America/Adak": "US", "America/Nome": "US", "America/Sitka": "US",
  "America/Yakutat": "US", "America/Metlakatla": "US", "America/Menominee": "US",
  "Pacific/Honolulu": "US",
  "America/Montevideo": "UY",
  "America/Caracas": "VE",
};

// Familias de zonas con muchas variantes internas.
const TIMEZONE_PREFIX_TO_COUNTRY: [string, string][] = [
  ["America/Argentina/", "AR"],
  ["America/Indiana/", "US"],
  ["America/Kentucky/", "US"],
  ["America/North_Dakota/", "US"],
];

const SUPPORTED = new Set(COUNTRIES.map((c) => c.code));

function fromTimezone(timeZone: string | undefined): string | null {
  if (!timeZone) return null;
  const direct = TIMEZONE_TO_COUNTRY[timeZone];
  if (direct) return direct;
  const prefixed = TIMEZONE_PREFIX_TO_COUNTRY.find(([prefix]) => timeZone.startsWith(prefix));
  return prefixed ? prefixed[1] : null;
}

function fromLocales(locales: readonly string[]): string | null {
  for (const locale of locales) {
    const region = locale.split(/[-_]/)[1]?.toUpperCase();
    if (region && SUPPORTED.has(region)) return region;
  }
  return null;
}

export function detectRegionLocally(
  timeZone: string | undefined = safeTimeZone(),
  locales: readonly string[] = safeLocales(),
): string {
  const byZone = fromTimezone(timeZone);
  if (byZone && SUPPORTED.has(byZone)) return byZone;
  return fromLocales(locales) ?? DEFAULT_REGION;
}

function safeTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

function safeLocales(): readonly string[] {
  try {
    const nav = typeof navigator !== "undefined" ? navigator : undefined;
    const fromNavigator = nav?.languages?.length ? nav.languages : nav?.language ? [nav.language] : [];
    return [...fromNavigator, Intl.DateTimeFormat().resolvedOptions().locale];
  } catch {
    return [];
  }
}

// País elegido a mano en el selector de Viva Cine. Antes viajaba en la URL
// (?region=XX), pero eso obligaba a renderizar /viva-cine/[slug] en una
// function de Netlify en cada visita (sin caché posible). Ahora la URL es
// una sola por película y la elección vive en el navegador del usuario.
const SAVED_REGION_KEY = "viva-cine:region";

export function getSavedRegion(): string | null {
  try {
    const saved = localStorage.getItem(SAVED_REGION_KEY);
    return saved && SUPPORTED.has(saved) ? saved : null;
  } catch {
    return null;
  }
}

export function saveRegion(region: string): void {
  try {
    localStorage.setItem(SAVED_REGION_KEY, region);
  } catch {
    // Modo privado / storage bloqueado: la elección dura solo esta página.
  }
}

// País elegido a mano en el selector global (lib/country-store.ts). Solo
// existe si la persona lo eligió: el país detectado no se guarda.
export const CHOSEN_COUNTRY_KEY = "cv-country";

export function getChosenCountry(): string | null {
  try {
    const saved = localStorage.getItem(CHOSEN_COUNTRY_KEY);
    return saved && SUPPORTED.has(saved) ? saved : null;
  } catch {
    return null;
  }
}

// Al cambiar el país global (lib/country-store.ts), Viva Cine vuelve a
// seguirlo en vez de quedarse con el catálogo de otro país elegido a mano.
export function clearSavedRegion(): void {
  try {
    localStorage.removeItem(SAVED_REGION_KEY);
  } catch {
    // Storage bloqueado: no había nada guardado.
  }
}
